import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
  OnInit,
} from '@angular/core';
import { ProfilePhotoService } from '../../../helpers/profile-photo.service';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { environment } from '../../../../environments/environment';
import { FormsServiceService } from '../../../api/services/forms-service.service';
import { CommonService } from '../../../api/services/common.service';
import { ToastrService } from 'ngx-toastr';
import { getAvatarBgColor } from '../../../helpers/avatar.helper';

@Component({
  selector: 'app-input-search-fields',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './input-search-fields.component.html',
  styleUrl: './input-search-fields.component.css',
})
export class InputSearchFieldsComponent implements OnInit {
  @Input() formGroup!: FormGroup;
  @Input() fields: any;
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  @Input() autoFocused: any;
  endpoint = environment.api_endpoint;
  fieldsData: any[] = [];
  showError: string = '';
  showErrors: boolean = false;
  userLoading: { [userId: string]: boolean } = {};
  allFieldsData: any[] = [];
  isLoading: boolean = true;
  formId: any;

  currentPage = 1;
  limit = 20;
  isLoadingMore = false;
  hasMoreData = true;
  searchText = '';

  constructor(
    private formService: FormsServiceService,
    private commonService: CommonService,
    private toaster: ToastrService,
    private cdr: ChangeDetectorRef,
  ) {}
  ngOnInit() {
    this.getData();
    const control = this.formGroup.get(this.fields.fieldName);
    if (control) {
      control.valueChanges.subscribe(() => {
        this.validateSelection();
        this.cdr.detectChanges();
      });
    }
  }

  ngOnChanges() {
    if (!this.autoFocused) return;

    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
    });
  }
  /** Function to get the data by api or static */
  getData() {
    if (this.fields.options.isStatic == false) {
      this.fieldsData = this.fields.options.staticOptions;
      this.isLoading = false;
      this.cdr.detectChanges();
    } else {
      this.formId = this.fields.options.source.formID;
      this.currentPage = 1;
      this.hasMoreData = true;
      this.loadData(this.formId, {
        query: '',
        page: this.currentPage,
        limit: this.limit,
      });
    }
  }

  applyFilters(searchText: string) {
    const pmSetup = JSON.parse(localStorage.getItem('pmSetup') || 'false');
    this.fieldsData = (this.allFieldsData || []).filter((option: any) => {
      const matchesFilters = pmSetup
        ? option.status === 'ACTIVE' && option.onPM === true
        : option.status === 'ACTIVE';
      const matchesSearch =
        !searchText ||
        option.name?.toLowerCase().includes(searchText.toLowerCase()) ||
        option.email?.toLowerCase().includes(searchText.toLowerCase());
      return matchesFilters && matchesSearch;
    });
    this.isLoading = false;
  }

  onKeyUpSearch(searchInput: any) {
    if (searchInput.length >= 3 || searchInput.length === 0) {
      this.searchText = searchInput;
      this.currentPage = 1;
      this.hasMoreData = true;
      this.loadData(this.formId, {
        query: searchInput,
        page: this.currentPage,
        limit: this.limit,
      });
    }
  }

  loadData(
    formId: any,
    pagination: { query: string; limit?: number; page?: number } = {
      query: '',
      page: 1,
      limit: 20,
    },
  ) {
    const pageNum = pagination.page || 1;
    if (pageNum === 1) {
      this.isLoading = true;
    } else {
      this.isLoadingMore = true;
    }

    const loadOptions = {
      query: pagination.query || '',
      limit: pagination.limit || this.limit,
      page: pageNum,
      status: 'ACTIVE',
      sort: 'name',
    };

    this.commonService.loadListWithOptions(formId, loadOptions).subscribe({
      next: (data) => {
        const records = data.data?.records || [];

        if (pageNum === 1) {
          this.allFieldsData = records;
        } else {
          this.allFieldsData = [...(this.allFieldsData || []), ...records];
        }

        if (records.length < (pagination.limit || this.limit)) {
          this.hasMoreData = false;
        } else {
          this.hasMoreData = true;
        }

        this.applyFilters(pagination.query);
        this.isLoading = false;
        this.isLoadingMore = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        if (pageNum === 1) {
          this.fieldsData = [];
          this.allFieldsData = [];
        }
        this.isLoading = false;
        this.isLoadingMore = false;
        this.cdr.detectChanges();
      },
    });
  }

  onScroll(event: Event) {
    if (this.fields?.options?.isStatic == false) return;

    const target = event.target as HTMLElement;
    if (target.scrollHeight - target.scrollTop - target.clientHeight < 10) {
      this.loadMore();
    }
  }

  loadMore() {
    if (this.isLoadingMore || !this.hasMoreData || !this.formId) return;
    this.currentPage++;
    this.loadData(this.formId, {
      query: this.searchText,
      page: this.currentPage,
      limit: this.limit,
    });
  }

  /** Show color for the user department wise */
  getBgColorByDepartment(name: string): string {
    return getAvatarBgColor(name);
  }

  getAssigneePhotoUrl(item: any): string | null {
    if (!item?.profilePhoto) {
      return './assets/media/icons/default-user-icon.png';
    }
    return `${item.profilePhoto}`;
  }

  handleImageError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = './assets/media/icons/default-user-icon.png';
  }

  resetSearch(): void {
    // const formId = FORM_ID.USERS;
    this.searchInput.nativeElement.value = '';
    this.searchText = '';
    this.currentPage = 1;
    this.hasMoreData = true;
    this.loadData(this.formId, {
      query: '',
      page: this.currentPage,
      limit: this.limit,
    });
    const control = this.formGroup?.get(this.fields.fieldName);
    if (control) {
      control.setValue(null);
      control.markAsPristine();
      control.markAsUntouched();
      control.updateValueAndValidity();
    }
    this.showError = '';
  }

  validateSelection(): boolean {
    const control = this.formGroup.get(this.fields.fieldName);
    if (!control?.value) {
      this.showError = 'Please select user';
      control?.setErrors({ required: true });
      return false;
    } else {
      this.showError = '';
      control?.setErrors(null);
      return true;
    }
  }

  /** Invite user with the details */
  inviteUser(item: any) {
    this.userLoading[item._id] = true;
    const payload = {
      fullName: item.name,
      email: item.email,
      invite: 'SENT',
      status: 'ACTIVE',
    };

    this.commonService.updateUserStatus(payload).subscribe({
      next: (res: any) => {
        this.toaster.success(res.message, 'Success', {
          messageClass: 'addMessage',
          titleClass: 'addCss',
        });
        this.userLoading[item._id] = false;
        setTimeout(() => {
          this.getData();
        }, 200);
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.toaster.error(err.message, 'Error', {
          messageClass: 'addMessage',
          titleClass: 'addCss',
        });
        this.userLoading[item._id] = false;
        this.cdr.detectChanges();
      },
    });
  }

  selectOption(option: any, event: MouseEvent) {
    if (option.status !== 'ACTIVE') return;

    const selection = window.getSelection()?.toString();
    if (selection && selection.length > 0) return;

    const control = this.formGroup.get(this.fields.fieldName);

    if (!control) return;

    control.setValue({
      email: option.email,
      name: option.name,
      _id: option._id,
      profilePhoto: option.profilePhoto,
    });

    // ✅ Important: mark properly
    control?.markAsDirty();
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }
}
